import Keycloak from 'keycloak-js';
import { createLogger } from './logger';
import { getBackendUrl } from './utils';
import { getEnv } from './runtimeConfig';

const logger = createLogger('keycloak');

// keycloak Configurations
const keycloakConfig = {
  url: getEnv('REACT_APP_KEYCLOAK_URL') || 'http://localhost:8080',
  realm: getEnv('REACT_APP_KEYCLOAK_REALM') || 'reality-coach-dev',
  clientId: getEnv('REACT_APP_KEYCLOAK_CLIENT_ID') || 'reality-coach-frontend',
};

// Create Keycloak instance
const keycloakInstance = new Keycloak(keycloakConfig);

// Expose for debugging (development only)
if (process.env.NODE_ENV === 'development') {
  (window as any).keycloakInstance = keycloakInstance;
  logger.debug('Keycloak instance exposed as window.keycloakInstance for debugging');
}

// Constants for token management
const TOKEN_EXPIRATION_BUFFER = 60; // 60 seconds buffer (reduced from 5 min to avoid rejecting fresh tokens)
export const TOKEN_STORAGE_KEY = 'keycloak_auth_token';

// Track initialization state
let isKeycloakInitialized = false;
let initializationPromise: Promise<boolean> | null = null;
let isMagicLinkAuth = false; // Flag to track magic link authentication
let isKeycloakNativeToken = false; // True when magic link token is a real Keycloak token (from token exchange)

// Export for external access
export { isMagicLinkAuth };

// Interval IDs for proactive refresh (prevent accumulation)
let proactiveRefreshInterval: ReturnType<typeof setInterval> | null = null;

// Inactivity tracking
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
let lastActivityTime = Date.now();
const updateLastActivity = () => { lastActivityTime = Date.now(); };
['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(event =>
  window.addEventListener(event, updateLastActivity, { passive: true })
);


// Memory token cache
let tokenCache: {
  token: string | null;
  refreshToken: string | null;
  // idToken is cached so it survives reloads. keycloak-js only appends id_token_hint to
  // the logout URL when keycloakInstance.idToken is set; without it Keycloak shows its
  // "Do you want to log out?" confirmation page instead of redirecting straight to login.
  idToken?: string | null;
  expirationTime: number;
  isMagicLink?: boolean;
  isKeycloakNative?: boolean;
} = {
  token: null,
  refreshToken: null,
  idToken: null,
  expirationTime: 0,
  isMagicLink: false,
  isKeycloakNative: false,
};

/**
 * Initialize Keycloak instance
 * @returns Promise resolving to boolean indicating if user is authenticated
 */
export const initKeycloak = async (onAuthSuccess?: () => void, onAuthError?: (error: any) => void): Promise<boolean> => {
  // Check for magic link token first
  const magicLinkToken = localStorage.getItem('magic_link_token');
  logger.debug('initKeycloak called - isInitialized:', isKeycloakInitialized, 'magicLinkToken present:', !!magicLinkToken, 'initializationPromise exists:', !!initializationPromise);

  // Return existing initialization promise if already in progress AND no magic link token
  if (initializationPromise && !magicLinkToken) {
    logger.debug('Keycloak initialization already in progress and no magic link token, returning cached promise');
    return initializationPromise;
  }

  // Return immediately if already initialized and no magic link token pending
  if (isKeycloakInitialized && !magicLinkToken) {
    logger.debug('Keycloak already initialized, returning current auth state');
    return keycloakInstance.authenticated || false;
  }

  // Create and cache the initialization promise
  initializationPromise = (async () => {
    try {
      logger.debug('Initializing Keycloak with config:', keycloakConfig);

      // First, check if we have a magic link token
      const magicLinkToken = localStorage.getItem('magic_link_token');
      if (magicLinkToken) {
        logger.debug('Found magic link token, initializing with magic link authentication');
        try {
          // Detect if this is a real Keycloak token (from token exchange) or legacy custom token
          const isNativeToken = isKeycloakNativeJwt(magicLinkToken);
          isKeycloakNativeToken = isNativeToken;
          logger.debug('Magic link token type:', isNativeToken ? 'Keycloak native (token exchange)' : 'Legacy custom-signed');

          // Initialize Keycloak with the magic link token
          keycloakInstance.token = magicLinkToken;
          keycloakInstance.tokenParsed = parseJwt(magicLinkToken);
          (keycloakInstance as any).authenticated = true;

          // Check for stored refresh token
          const magicLinkRefreshToken = localStorage.getItem('magic_link_refresh_token');
          if (magicLinkRefreshToken) {
            keycloakInstance.refreshToken = magicLinkRefreshToken;
            logger.debug('Using stored refresh token for magic link authentication');
          } else {
            logger.warn('No refresh token found for magic link authentication');
          }

          // Mark this as a magic link authentication
          isMagicLinkAuth = true;

          // Update token cache with refresh token if available
          updateTokenCache(magicLinkToken, magicLinkRefreshToken);

          // Note: localStorage cleanup is handled by MagicLinkAuth.tsx's finally block

          logger.debug('Magic link authentication successful, token parsed:', !!keycloakInstance.tokenParsed);

          // IMPORTANT: Mark as initialized BEFORE setting up refresh to prevent race conditions
          isKeycloakInitialized = true;

          // Set up automatic token refresh
          if (magicLinkRefreshToken) {
            setupTokenRefresh();
            setupProactiveRefresh();
          }

          onAuthSuccess?.();
          return true;
        } catch (error) {
          logger.error('Magic link token validation failed:', error);
          // Note: localStorage cleanup is handled by MagicLinkAuth.tsx's finally block
          onAuthError?.(error);
          return false;
        }
      }

      // Then, check if we have cached tokens from a previous session
      const cachedToken = initTokenFromStorage();
      if (cachedToken) {
        logger.debug('Found cached token, isMagicLink:', isMagicLinkAuth);
        
        // If this is a cached magic link token, restore it directly
        if (isMagicLinkAuth) {
          logger.debug('Restoring cached magic link authentication, isKeycloakNative:', isKeycloakNativeToken);
          keycloakInstance.token = tokenCache.token!;
          keycloakInstance.refreshToken = tokenCache.refreshToken || undefined;
          keycloakInstance.tokenParsed = parseJwt(tokenCache.token!);
          // Restore idToken too so logout() can supply id_token_hint and skip Keycloak's prompt.
          keycloakInstance.idToken = tokenCache.idToken || undefined;
          (keycloakInstance as any).authenticated = true;

          // IMPORTANT: Mark as initialized early to prevent repeated initialization
          isKeycloakInitialized = true;

          // Try to refresh the token if it's expired or expiring soon
          const now = Date.now();
          const expirationThreshold = now + (TOKEN_EXPIRATION_BUFFER * 1000);
          if (tokenCache.expirationTime <= expirationThreshold && tokenCache.refreshToken) {
            logger.debug('Cached magic link token is expired or expiring soon, attempting refresh');
            try {
              if (isKeycloakNativeToken) {
                // Use standard Keycloak refresh for native tokens
                const refreshed = await keycloakInstance.updateToken(TOKEN_EXPIRATION_BUFFER);
                if (refreshed && keycloakInstance.token) {
                  logger.debug('Keycloak-native magic link token refreshed during init');
                  updateTokenCache(keycloakInstance.token, keycloakInstance.refreshToken || null);
                }
              } else {
                // Use backend refresh for legacy tokens
                const newToken = await refreshMagicLinkToken(tokenCache.token!, tokenCache.refreshToken);
                if (newToken) {
                  logger.debug('Legacy magic link token refreshed during init');
                } else {
                  logger.debug('Legacy magic link token refresh failed during init, keeping cached token');
                }
              }
            } catch (refreshError) {
              logger.debug('Magic link token refresh error during init:', refreshError);
            }
          }

          // Set up automatic token refresh for cached magic links
          if (tokenCache.refreshToken) {
            logger.debug('Setting up token refresh for cached magic link session');
            setupTokenRefresh();
            setupProactiveRefresh();
          }

          onAuthSuccess?.();
          return true;
        }
        
        // For regular cached tokens, try check-sso first
        logger.debug('Found cached regular token, attempting to restore authentication state');
        // Try to initialize without automatic auth check
        try {
          const authenticated = await keycloakInstance.init({
            // Remove onLoad to prevent iframe creation
            // onLoad: 'check-sso',
            // Disable silent SSO to avoid iframe sandbox security issues
            // silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
            pkceMethod: 'S256',
            checkLoginIframe: false,
          });

          if (authenticated) {
            logger.debug('Keycloak init successful');
            // Cache the token if we got one
            if (keycloakInstance.token && keycloakInstance.tokenParsed) {
              updateTokenCache(keycloakInstance.token, keycloakInstance.refreshToken || null);
            }
            onAuthSuccess?.();
          } else {
            logger.debug('Keycloak init completed, checking cached tokens');
            // Restore authentication state from cached token
            keycloakInstance.token = tokenCache.token!;
            keycloakInstance.refreshToken = tokenCache.refreshToken || undefined;
            keycloakInstance.tokenParsed = parseJwt(tokenCache.token!);
            // Restore idToken too so logout() can supply id_token_hint and skip Keycloak's prompt.
            keycloakInstance.idToken = tokenCache.idToken || undefined;
            (keycloakInstance as any).authenticated = true;

            // Try to refresh the token if it's expired or expiring soon
            const now = Date.now();
            const expirationThreshold = now + (TOKEN_EXPIRATION_BUFFER * 1000);
            if (tokenCache.expirationTime <= expirationThreshold && tokenCache.refreshToken) {
              logger.debug('Cached token is expired or expiring soon, attempting refresh');
              try {
                const refreshed = await keycloakInstance.updateToken(TOKEN_EXPIRATION_BUFFER);
                if (refreshed && keycloakInstance.token) {
                  logger.debug('Token refreshed successfully during init');
                  updateTokenCache(keycloakInstance.token, keycloakInstance.refreshToken || null);
                } else {
                  logger.debug('Token refresh failed during init');
                  // Keep the cached token anyway - it might still be usable
                }
              } catch (refreshError) {
                logger.debug('Token refresh error during init:', refreshError);
                // Keep the cached token anyway
              }
            }

            onAuthSuccess?.();
          }
        } catch (error) {
          logger.debug('Keycloak init error, restoring from cached token:', error);
          // Restore authentication state from cached token
          keycloakInstance.token = tokenCache.token!;
          keycloakInstance.refreshToken = tokenCache.refreshToken || undefined;
          keycloakInstance.tokenParsed = parseJwt(tokenCache.token!);
          // Restore idToken too so logout() can supply id_token_hint and skip Keycloak's prompt.
          keycloakInstance.idToken = tokenCache.idToken || undefined;
          (keycloakInstance as any).authenticated = true;

          // Try to refresh the token
          if (tokenCache.refreshToken) {
            logger.debug('Attempting token refresh after init error');
            try {
              const refreshed = await keycloakInstance.updateToken(TOKEN_EXPIRATION_BUFFER);
              if (refreshed && keycloakInstance.token) {
                logger.debug('Token refreshed successfully after init error');
                updateTokenCache(keycloakInstance.token, keycloakInstance.refreshToken || null);
              }
            } catch (refreshError) {
              logger.debug('Token refresh failed after init error:', refreshError);
            }
          }

          onAuthSuccess?.();
        }
      } else {
        // No cached token, do normal initialization without automatic auth check
        const authenticated = await keycloakInstance.init({
          // Remove onLoad to prevent iframe creation
          // onLoad: 'check-sso',
          // Disable silent SSO to avoid iframe sandbox security issues
          // silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
          pkceMethod: 'S256',
          checkLoginIframe: false,
        });

        // Since we removed onLoad, init will return false
        // We rely on cached tokens for authentication state
        logger.debug('Keycloak init completed without onLoad, authenticated:', authenticated);
        if (!authenticated) {
          logger.debug('No automatic authentication, checking for cached tokens');
          // Try to restore from cache if available
          const cachedToken = initTokenFromStorage();
          if (cachedToken) {
            logger.debug('Restoring authentication from cached token');
            keycloakInstance.token = tokenCache.token!;
            keycloakInstance.refreshToken = tokenCache.refreshToken || undefined;
            keycloakInstance.tokenParsed = parseJwt(tokenCache.token!);
            // Restore idToken too so logout() can supply id_token_hint and skip Keycloak's prompt.
            keycloakInstance.idToken = tokenCache.idToken || undefined;
            (keycloakInstance as any).authenticated = true;

            // Try to refresh the token if needed
            const now = Date.now();
            const expirationThreshold = now + (TOKEN_EXPIRATION_BUFFER * 1000);
            if (tokenCache.expirationTime <= expirationThreshold && tokenCache.refreshToken) {
              try {
                const refreshed = await keycloakInstance.updateToken(TOKEN_EXPIRATION_BUFFER);
                if (refreshed && keycloakInstance.token) {
                  updateTokenCache(keycloakInstance.token, keycloakInstance.refreshToken || null);
                }
              } catch (refreshError) {
                logger.debug('Token refresh failed:', refreshError);
              }
            }

            onAuthSuccess?.();
            return true;
          }
        }

        if (authenticated) {
          logger.debug('User is authenticated');
          logger.debug('Token present:', !!keycloakInstance.token);
          logger.debug('Token parsed:', !!keycloakInstance.tokenParsed);
          
          // Cache the initial token
          if (keycloakInstance.token && keycloakInstance.tokenParsed) {
            logger.debug('Caching token with expiration:', keycloakInstance.tokenParsed.exp);
            updateTokenCache(keycloakInstance.token, keycloakInstance.refreshToken || null);
          } else {
            logger.warn('Token missing after authentication!');
          }
          onAuthSuccess?.();
        } else {
          logger.debug('User is not authenticated');
        }

        return authenticated;
      }

      // Set up token refresh
      setupTokenRefresh();
      setupProactiveRefresh();

      // Mark as initialized
      isKeycloakInitialized = true;
      logger.debug('Keycloak initialization complete');

      return keycloakInstance.authenticated || false;
    } catch (error) {
      logger.error('Failed to initialize Keycloak:', error);

      // Even if initialization fails, try to restore from cache as fallback
      const cachedToken = initTokenFromStorage();
      if (cachedToken) {
        logger.debug('Restoring authentication from cache after init failure');
        keycloakInstance.token = tokenCache.token!;
        keycloakInstance.refreshToken = tokenCache.refreshToken || undefined;
        keycloakInstance.tokenParsed = parseJwt(tokenCache.token!);
        (keycloakInstance as any).authenticated = true;
        isKeycloakInitialized = true;
        onAuthSuccess?.();
        return true;
      }

      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          logger.warn('Keycloak server responded with 401 - this is normal if user is not logged in');
          logger.warn('Make sure:');
          logger.warn('1. Keycloak server is running on', keycloakConfig.url);
          logger.warn('2. Realm "reality-coach-dev" exists');
          logger.warn('3. Client "reality-coach-frontend" is configured as public client');
          // Don't call onAuthError for 401 - this is expected when not logged in
          return false;
        } else if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch') || error.message.includes('CORS') || error.message.includes('404')) {
          logger.warn('Cannot connect to Keycloak server at', keycloakConfig.url);
          logger.warn('Possible issues:');
          logger.warn('- Keycloak server not running');
          logger.warn('- CORS not configured properly');
          logger.warn('- Wrong URL or realm name');
          logger.warn('- Client not configured as public client');
          onAuthError?.(new Error('Cannot connect to Keycloak server. Please check server configuration and CORS settings.'));
        } else {
          onAuthError?.(error);
        }
      } else {
        onAuthError?.(error);
      }

      return false;
    }
  })();

  return initializationPromise;
};

/**
 * Setup automatic token refresh
 */
const setupTokenRefresh = () => {
  // Refresh token before it expires
  keycloakInstance.onTokenExpired = () => {
    logger.debug('Token expired, refreshing...');

    // For legacy custom magic link tokens, skip Keycloak refresh
    // (handled by backend refresh endpoint instead)
    // For Keycloak-native magic link tokens, use standard Keycloak refresh
    if (isMagicLinkAuth && !isKeycloakNativeToken) {
      logger.debug('Skipping Keycloak token refresh for legacy magic link authentication');
      return;
    }

    keycloakInstance.updateToken(TOKEN_EXPIRATION_BUFFER)
      .then((refreshed: boolean) => {
        if (refreshed) {
          logger.debug('Token refreshed successfully');
          if (keycloakInstance.token) {
            updateTokenCache(keycloakInstance.token, keycloakInstance.refreshToken || null);
          }
        }
      })
      .catch((error: any) => {
        logger.error('Failed to refresh token:', error);
        clearStoredToken();
      });
  };
};

// Tracks whether a proactive refresh run is already in flight, so the interval tick
// and the visibilitychange listener (added below) can't both kick off overlapping requests.
let proactiveRefreshInProgress = false;

/**
 * Runs one proactive-refresh check (inactivity logout, then magic-link/Keycloak token
 * refresh). Shared by the 15-minute interval and the visibilitychange listener, so a
 * backgrounded tab that missed interval ticks (browsers throttle setInterval when a tab
 * isn't focused) still gets checked the moment it becomes visible again — instead of
 * only discovering an expired refresh token on the next real API call.
 *
 * `fromVisibilityRestore` skips the inactivity check: `lastActivityTime` is only bumped by
 * mouse/keyboard/scroll on `window`, none of which fire while the tab is backgrounded or the
 * machine is asleep. Without this, returning to a tab left hidden past INACTIVITY_TIMEOUT_MS
 * would hit the inactivity branch and log the user out immediately — the opposite of what the
 * visibility listener exists to prevent. Coming back to the tab is itself a sign of activity.
 */
const runProactiveRefresh = async (fromVisibilityRestore = false) => {
  if (proactiveRefreshInProgress) {
    logger.debug('Proactive refresh already in progress, skipping this trigger');
    return;
  }
  proactiveRefreshInProgress = true;
  try {
    if (fromVisibilityRestore) {
      lastActivityTime = Date.now();
    } else if (Date.now() - lastActivityTime > INACTIVITY_TIMEOUT_MS) {
      logger.debug('Proactive refresh skipped: user inactive, logging out');
      await logout();
      return;
    }

    // For legacy custom magic link tokens, use backend refresh endpoint
    // For Keycloak-native magic link tokens, fall through to standard Keycloak refresh
    if (isMagicLinkAuth && !isKeycloakNativeToken) {
      logger.debug('Proactive refresh for legacy magic link authentication - calling backend');

      if (tokenCache.refreshToken && keycloakInstance.authenticated) {
        try {
          const refreshedToken = await refreshMagicLinkToken(tokenCache.token!, tokenCache.refreshToken);
          if (!refreshedToken) {
            logger.error('Refresh token expired or invalid, logging out user');
            await logout();
          }
        } catch (error) {
          logger.error('Error during magic link proactive refresh:', error);
        }
      } else {
        logger.debug('No refresh token available for magic link proactive refresh');
      }
      return;
    }

    // For regular Keycloak authentication AND Keycloak-native magic link tokens,
    // use standard Keycloak refresh
    if (keycloakInstance.authenticated && keycloakInstance.token) {
      logger.debug('Proactive token refresh via Keycloak');
      try {
        const refreshed = await keycloakInstance.updateToken(24 * 60 * 60); // Force refresh (24 hours in seconds)
        if (refreshed && keycloakInstance.token) {
          logger.debug('Proactive token refresh successful');
          updateTokenCache(keycloakInstance.token, keycloakInstance.refreshToken || null);
        } else {
          logger.debug('Proactive token refresh: token still valid');
        }
      } catch (error) {
        logger.error('Proactive token refresh failed:', error);
      }
    }
  } finally {
    proactiveRefreshInProgress = false;
  }
};

/**
 * Setup proactive token refresh: a 15-minute interval as the baseline, plus a
 * visibilitychange listener that runs the same check immediately when the tab becomes
 * visible again. Browsers throttle/pause setInterval timers in backgrounded tabs, so
 * without this, a tab left in the background can silently miss enough 15-minute ticks
 * for the (legacy magic-link) refresh token to expire — the user only finds out when
 * some unrelated API call fails and force-logs-out. Re-checking on visibility restores
 * catches that before it happens.
 */
const handleVisibilityChangeForRefresh = () => {
  if (document.visibilityState === 'visible') {
    logger.debug('Tab became visible, running proactive refresh check');
    runProactiveRefresh(true).catch((error) => {
      logger.error('Proactive refresh on visibility restore failed:', error);
    });
  }
};

const setupProactiveRefresh = () => {
  if (proactiveRefreshInterval) {
    clearInterval(proactiveRefreshInterval);
  }
  document.removeEventListener('visibilitychange', handleVisibilityChangeForRefresh);
  document.addEventListener('visibilitychange', handleVisibilityChangeForRefresh);

  proactiveRefreshInterval = setInterval(() => {
    runProactiveRefresh().catch((error) => {
      logger.error('Proactive refresh interval tick failed:', error);
    });
  }, 15 * 60 * 1000); // refresh token in every 15 minutes
};

/**
 * Update token cache
 */
const updateTokenCache = (token: string, refreshToken: string | null) => {
  const now = Date.now();
  // Store the actual expiration time (already in milliseconds after conversion)
  // We'll apply the buffer when CHECKING expiration, not when STORING it
  const expirationTime = keycloakInstance.tokenParsed?.exp 
    ? (keycloakInstance.tokenParsed.exp * 1000) // Just convert to milliseconds
    : now + (60 * 60 * 1000); // Default to 1 hour

  logger.debug('Caching token - expires at:', new Date(expirationTime).toISOString(), 
              'Minutes from now:', ((expirationTime - now) / 1000 / 60).toFixed(2));

  tokenCache = {
    token,
    refreshToken,
    // Preserve the id token across caches/reloads so logout() can supply id_token_hint.
    // keycloakInstance.idToken is set by keycloak-js after a real login; fall back to the
    // previously cached value (e.g. on a token refresh that doesn't return a new id token).
    idToken: keycloakInstance.idToken || tokenCache.idToken || null,
    expirationTime,
    isMagicLink: isMagicLinkAuth,
    isKeycloakNative: isKeycloakNativeToken,
  };

  // Store in localStorage for persistence
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenCache));
  } catch (error) {
    logger.error('Error storing token in localStorage:', error);
  }
};

/**
 * Get the current user's Keycloak token with intelligent caching
 * @returns A promise resolving to the Keycloak token
 */
export const getIdToken = async (): Promise<string> => {
  // Wait for Keycloak to initialize if not yet done
  if (!isKeycloakInitialized && initializationPromise) {
    logger.debug('Waiting for Keycloak initialization to complete...');
    await initializationPromise;
  }

  const now = Date.now();
  // Apply buffer when checking expiration: refresh 60s before actual expiry
  const expirationThreshold = now + (TOKEN_EXPIRATION_BUFFER * 1000);


  // For magic link authentication, handle expiration differently
  if (isMagicLinkAuth) {
    // If token is still valid, return it
    if (tokenCache.token && tokenCache.expirationTime > expirationThreshold) {
      const minutesLeft = (tokenCache.expirationTime - now) / 1000 / 60;
      logger.debug(`Returning magic link token (${minutesLeft.toFixed(2)} minutes left)`);
      return tokenCache.token;
    }

    // Token expired — try to refresh
    if (tokenCache.refreshToken) {
      // For Keycloak-native tokens, try standard Keycloak refresh first, then fall back to backend API
      if (isKeycloakNativeToken) {
        logger.debug('Keycloak-native magic link token expired, using standard Keycloak refresh');
        try {
          const refreshed = await keycloakInstance.updateToken(TOKEN_EXPIRATION_BUFFER);
          if (refreshed && keycloakInstance.token) {
            logger.debug('Keycloak-native magic link token refreshed successfully');
            updateTokenCache(keycloakInstance.token, keycloakInstance.refreshToken || null);
            return keycloakInstance.token;
          }
        } catch (refreshError) {
          logger.warn('Keycloak-native refresh failed, falling back to backend /auth/refresh:', refreshError);
          // Fall back to backend refresh API (same path as legacy tokens)
          try {
            const refreshedToken = await refreshMagicLinkToken(tokenCache.token!, tokenCache.refreshToken!);
            if (refreshedToken) {
              logger.debug('Keycloak-native token refreshed via backend fallback');
              return refreshedToken;
            }
          } catch (fallbackError) {
            logger.error('Backend fallback refresh also failed:', fallbackError);
          }
          await logout('/session-expired');
          throw new Error('MAGIC_LINK_REFRESH_ERROR');
        }
      } else {
        // For legacy custom tokens, call backend refresh API
        logger.debug('Legacy magic link token expired, calling backend refresh API');
        try {
          const refreshedToken = await refreshMagicLinkToken(tokenCache.token!, tokenCache.refreshToken!);
          if (refreshedToken) {
            logger.debug('Legacy magic link token refreshed successfully via backend');
            return refreshedToken;
          } else {
            logger.error('Backend refresh returned null');
            await logout('/session-expired');
            throw new Error('MAGIC_LINK_REFRESH_FAILED');
          }
        } catch (refreshError) {
          logger.error('Legacy magic link token backend refresh failed:', refreshError);
          await logout('/session-expired');
          throw new Error('MAGIC_LINK_REFRESH_ERROR');
        }
      }
    }

    // No refresh token available, session expired
    logger.warn('Magic link token expired and no valid refresh token');
    await logout('/session-expired');
    throw new Error('MAGIC_LINK_EXPIRED');
  }

  // Check if we have a valid cached token in memory (with buffer)
  if (tokenCache.token && tokenCache.expirationTime > expirationThreshold) {
    return tokenCache.token;
  }

  // Check if user is authenticated
  if (!keycloakInstance.authenticated) {
    logger.warn('Keycloak instance not authenticated, trying localStorage...');
    
    // Try to get from localStorage as a fallback
    try {
      const storedCache = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (storedCache) {
        const parsedCache = JSON.parse(storedCache);
        const minutesLeft = (parsedCache.expirationTime - now) / 1000 / 60;
        logger.debug(`Token in localStorage - ${minutesLeft.toFixed(2)} minutes left`);
        
        if (parsedCache.expirationTime > expirationThreshold) {
          logger.debug('Found valid token in localStorage');
          tokenCache = parsedCache;
          return parsedCache.token;
        } else if (parsedCache.refreshToken) {
          logger.debug('Token expired but has refresh token, attempting refresh');
          // Restore the tokens to the instance first
          keycloakInstance.token = parsedCache.token;
          keycloakInstance.refreshToken = parsedCache.refreshToken;
          keycloakInstance.tokenParsed = parseJwt(parsedCache.token);
          // Restore idToken too so logout() can supply id_token_hint and skip Keycloak's prompt.
          keycloakInstance.idToken = parsedCache.idToken || undefined;
          (keycloakInstance as any).authenticated = true;
          
          // Try to refresh
          try {
            const refreshed = await keycloakInstance.updateToken(TOKEN_EXPIRATION_BUFFER);
            if (refreshed && keycloakInstance.token) {
              logger.debug('Token refreshed successfully');
              updateTokenCache(keycloakInstance.token, keycloakInstance.refreshToken || null);
              return keycloakInstance.token;
            }
          } catch (refreshError) {
            logger.debug('Token refresh failed:', refreshError);
            // Clear the invalid tokens
            clearStoredToken();
          }
        } else {
          logger.debug('Token in localStorage expired or expiring soon');
          localStorage.removeItem(TOKEN_STORAGE_KEY);
        }
      } else {
        logger.debug('No token in localStorage');
      }
    } catch (error) {
      logger.error('Error reading from localStorage:', error);
    }
    
    logger.error('No valid token found - user needs to login');
    throw new Error("No user is currently logged in");
  }
  
  // If authenticated and token is still valid, return it
  if (keycloakInstance.token) {
    const tokenExp = keycloakInstance.tokenParsed?.exp;
    if (tokenExp && (tokenExp * 1000) > expirationThreshold) {
      logger.debug('Using current Keycloak token (still valid)');
      updateTokenCache(keycloakInstance.token, keycloakInstance.refreshToken || null);
      return keycloakInstance.token;
    }
    logger.debug('Keycloak token expired or expiring soon, attempting refresh...');
  }

  // Token expired or missing — attempt refresh
  try {
    const refreshed = await keycloakInstance.updateToken(TOKEN_EXPIRATION_BUFFER);

    if (refreshed && keycloakInstance.token) {
      logger.debug('Token refreshed successfully via updateToken');
      updateTokenCache(keycloakInstance.token, keycloakInstance.refreshToken || null);
      return keycloakInstance.token;
    }

    // updateToken returned false (token still valid) but we got here because
    // our expiration check was stricter — return the current token
    if (keycloakInstance.token) {
      updateTokenCache(keycloakInstance.token, keycloakInstance.refreshToken || null);
      return keycloakInstance.token;
    }

    throw new Error("Failed to get valid token after refresh");
  } catch (error) {
    logger.error('Failed to refresh token:', error);
    // Clear stored tokens so user gets redirected to login
    clearStoredToken();
    throw error;
  }
};

/**
 * Get the Keycloak instance
 */
export const getAuthInstance = () => {
  return keycloakInstance;
};

/**
 * Login with Keycloak
 */
export const login = async (redirectUri?: string) => {
  try {
    await keycloakInstance.login({
      redirectUri: redirectUri || window.location.origin
    });
  } catch (error) {
    logger.error('Login failed:', error);
    throw error;
  }
};

/**
 * Login with Google SSO via Keycloak identity provider
 */
export const loginWithGoogle = async (redirectUri?: string) => {
  try {
    await keycloakInstance.login({
      redirectUri: redirectUri || window.location.origin,
      idpHint: 'google' // This tells Keycloak to use the Google identity provider
    });
  } catch (error) {
    logger.error('Google login failed:', error);
    throw error;
  }
};

/**
 * Login with username and password using Resource Owner Password Credentials grant
 * This requires the grant to be enabled in Keycloak client configuration
 */
export const loginWithCredentials = async (username: string, password: string): Promise<boolean> => {
  try {
    // Ensure Keycloak is initialized first
    if (!isKeycloakInitialized) {
      logger.debug('Keycloak not initialized, initializing first...');
      await initKeycloak();
    }

    const tokenEndpoint = `${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/token`;

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'password',
        client_id: keycloakConfig.clientId,
        username: username,
        password: password,
        scope: 'openid profile email',
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error_description || `Login failed: ${response.status}`);
    }

    const tokenData = await response.json();

    // Manually set the token in the Keycloak instance
    keycloakInstance.token = tokenData.access_token;
    keycloakInstance.refreshToken = tokenData.refresh_token;
    keycloakInstance.tokenParsed = parseJwt(tokenData.access_token);

    // Capture the id token so logout can supply id_token_hint. This direct-grant path
    // sets tokens manually (keycloak-js never runs its own flow here), so without this
    // keycloakInstance.idToken stays undefined and Keycloak shows its "Do you want to
    // log out?" confirmation page instead of redirecting straight to login.
    if (tokenData.id_token) {
      keycloakInstance.idToken = tokenData.id_token;
      keycloakInstance.idTokenParsed = parseJwt(tokenData.id_token);
    }

    // Reset magic link flags since this is a normal login
    isMagicLinkAuth = false;
    isKeycloakNativeToken = false;

    // Mark as authenticated
    (keycloakInstance as any).authenticated = true;

    // Cache the token
    updateTokenCache(tokenData.access_token, tokenData.refresh_token);

    // Set up token refresh
    setupTokenRefresh();
    setupProactiveRefresh();

    logger.debug('Direct login successful');
    return true;
  } catch (error) {
    logger.error('Direct login failed:', error);
    throw error;
  }
};

/**
 * parse JWT token to get payload
 */
const parseJwt = (token: string): any => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  } catch (error) {
    logger.error('Failed to parse JWT:', error);
    return null;
  }
};

/**
 * Detect if a JWT token is a real Keycloak-issued token (from token exchange)
 * vs a custom-signed legacy token.
 *
 * Keycloak-native tokens have:
 * - typ: "Bearer" in the header
 * - azp claim (authorized party)
 * - session_state claim
 * - No token_type: "magic_link" in the payload
 *
 * Legacy custom tokens have:
 * - token_type: "magic_link" in the payload
 */
const isKeycloakNativeJwt = (token: string): boolean => {
  try {
    const parsed = parseJwt(token);
    if (!parsed) return false;

    // Legacy custom tokens explicitly set token_type to "magic_link"
    if (parsed.token_type === 'magic_link' || parsed.token_type === 'refresh') {
      return false;
    }

    // Keycloak tokens have typ: "Bearer" and azp claim
    if (parsed.typ === 'Bearer' || parsed.azp) {
      return true;
    }

    // Also check for session_state which Keycloak always includes
    if (parsed.session_state) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
};

// The backend rotates the refresh token on every /auth/refresh call, so if two callers
// race with the same (now-stale) refresh token, only one wins and the other gets a 401
// or an invalid-token error. Sharing one in-flight promise makes concurrent callers
// (e.g. several API calls firing at once right as the token expires) await the same
// backend call and reuse its result instead of each spending the old refresh token.
let magicLinkRefreshInFlight: Promise<string | null> | null = null;

/**
 * Refresh magic link token using backend API
 */
const refreshMagicLinkToken = async (token: string, refreshToken: string): Promise<string | null> => {
  if (magicLinkRefreshInFlight) {
    logger.debug('Magic link refresh already in flight, awaiting existing request');
    return magicLinkRefreshInFlight;
  }

  magicLinkRefreshInFlight = (async () => {
    try {
      logger.debug('🔄 Refreshing magic link token via backend API...');

      const backendUrl = getEnv('REACT_APP_BACKEND_URL') || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refresh_token: refreshToken
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('❌ Backend refresh request failed:', response.status, errorText);
        return null;
      }

      const data = await response.json();

      if (data.success && data.access_token) {
        logger.debug('✅ Magic link token refreshed successfully via backend');

        // Update Keycloak instance with new tokens
        keycloakInstance.token = data.access_token;
        keycloakInstance.tokenParsed = parseJwt(data.access_token);
        keycloakInstance.refreshToken = data.refresh_token;

        // Update cache with new tokens
        updateTokenCache(data.access_token, data.refresh_token);

        return data.access_token;
      } else {
        logger.error('❌ Backend refresh response invalid:', data);
        return null;
      }
    } catch (error) {
      logger.error('❌ Error refreshing magic link token via backend:', error);
      return null;
    } finally {
      magicLinkRefreshInFlight = null;
    }
  })();

  return magicLinkRefreshInFlight;
};

// Redirect target for LIFT users when their session expires
/**
 * Logout from Keycloak
 */
export const logout = async (redirectUri?: string) => {
  try {
    const wasMagicLink = isMagicLinkAuth;

    // Restore the id token onto the instance BEFORE clearStoredToken() wipes the cache.
    // keycloak-js appends id_token_hint to the logout URL only when idToken is present;
    // without it Keycloak (18+) shows its "Do you want to log out?" confirmation page
    // instead of redirecting straight to the login page. After a reload/cache-restore the
    // instance loses idToken, so we re-hydrate it from the cache here.
    if (!keycloakInstance.idToken && tokenCache.idToken) {
      keycloakInstance.idToken = tokenCache.idToken;
    }

    // Capture what we need for logout BEFORE clearStoredToken() wipes the cache.
    const idTokenHint = keycloakInstance.idToken || tokenCache.idToken || null;
    const refreshToken = keycloakInstance.refreshToken || tokenCache.refreshToken || null;
    const target = redirectUri || window.location.origin + '/login';

    clearStoredToken();
    isMagicLinkAuth = false;
    isKeycloakNativeToken = false;

    // Magic link (LIFT) users are redirected back to LIFT on session expiry.
    if (wasMagicLink) {
      const liftUrl = getEnv('REACT_APP_LIFT_REDIRECT_URL') || window.location.origin + '/login';
      window.location.href = liftUrl;
      return;
    }

    // Standard path: we have an id_token_hint, so Keycloak logs out silently and
    // redirects straight to the login page — no confirmation prompt.
    if (idTokenHint) {
      await keycloakInstance.logout({ redirectUri: target });
      return;
    }

    // No id_token_hint available (e.g. a session restored from cache that predates id-token
    // caching, or a token-exchange/native session that never had an id token). Routing such a
    // session through Keycloak's interactive logout endpoint would force its "Do you want to
    // log out?" confirmation page. Instead we revoke the server session silently via the
    // refresh token (no browser interaction, no prompt) and redirect straight to login.
    logger.warn('Logout without id_token_hint — using silent back-channel logout to avoid Keycloak confirmation page');
    await silentBackchannelLogout(refreshToken);
    window.location.href = target;
  } catch (error) {
    logger.error('Logout failed:', error);
    throw error;
  }
};

/**
 * Best-effort server-side logout with no browser redirect or confirmation prompt.
 *
 * Posts the refresh token to Keycloak's end-session endpoint, which revokes the SSO
 * session via the back channel. Used only when we have no id_token_hint to drive the
 * normal redirect logout — without the hint, the interactive endpoint would otherwise
 * show Keycloak's "Do you want to log out?" page. reality-coach-frontend is a public
 * client (PKCE), so client_id + refresh_token is sufficient (no client secret).
 */
const silentBackchannelLogout = async (refreshToken: string | null): Promise<void> => {
  if (!refreshToken) {
    logger.debug('No refresh token available for back-channel logout; skipping server-side revoke');
    return;
  }
  try {
    const logoutEndpoint = `${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/logout`;
    const body = new URLSearchParams({
      client_id: keycloakConfig.clientId,
      refresh_token: refreshToken,
    });
    await fetch(logoutEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    logger.debug('Back-channel logout request sent to Keycloak');
  } catch (error) {
    // Non-fatal: we still redirect to login. Worst case the SSO session lingers until it expires.
    logger.warn('Silent back-channel logout failed (continuing to login redirect):', error);
  }
};

/**
 * Get current user information from Keycloak token
 */
export const getCurrentUser = () => {
  if (!keycloakInstance.authenticated || !keycloakInstance.tokenParsed) {
    return null;
  }

  return {
    id: keycloakInstance.tokenParsed.sub || '',
    email: keycloakInstance.tokenParsed.email || '',
    display_name: keycloakInstance.tokenParsed.name || keycloakInstance.tokenParsed.preferred_username || '',
    username: keycloakInstance.tokenParsed.preferred_username || '',
    roles: keycloakInstance.tokenParsed.realm_access?.roles || [],
  };
};

/**
 * Check if user has a specific role
 */
export const hasRole = (role: string): boolean => {
  return keycloakInstance.hasRealmRole(role) || keycloakInstance.hasResourceRole(role);
};

/**
 * Get user roles
 */
export const getUserRoles = (): string[] => {
  if (!keycloakInstance.tokenParsed) {
    return [];
  }
  return keycloakInstance.tokenParsed.realm_access?.roles || [];
};

/**
 * Clears the stored authentication token from both memory cache and localStorage
 */
export const clearStoredToken = (): void => {
  // Clear memory cache
  tokenCache = {
    token: null,
    refreshToken: null,
    expirationTime: 0
  };

  // Remove specific keys from localStorage
  const keysToRemove = [
    'keycloak_auth_token',
    'preferred-avatar-type',
    'rolePlay_LastRefreshed',
    'rolePlay_Options',
    'role'
  ];

  try {
    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    logger.error('Error clearing specific keys from localStorage:', error);
  }
};

/**
 * Initializes token from localStorage if available
 * Returns the token if found (even if expired), otherwise null
 * Expired tokens can still be refreshed if they have refresh tokens
 */
export const initTokenFromStorage = (): string | null => {
  try {
    const storedCache = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (storedCache) {
      const parsedCache = JSON.parse(storedCache);
      // Always restore from cache if it exists - let refresh logic handle expiration
      // Only remove if there's no refresh token and the token is expired
      const now = Date.now();
      const isExpired = parsedCache.expirationTime <= now;

      if (isExpired && !parsedCache.refreshToken) {
        // Only clear if expired AND no refresh token
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        return null;
      }

      // Update memory cache
      tokenCache = parsedCache;
      // Restore the magic link flag and native token flag
      isMagicLinkAuth = parsedCache.isMagicLink || false;
      isKeycloakNativeToken = parsedCache.isKeycloakNative || false;
      return parsedCache.token;
    }
  } catch (error) {
    logger.error('Error reading token from localStorage:', error);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }

  return null;
};

/**
 * Send password reset email
 * Calls backend API to send password reset email via Keycloak
 */
export const sendPasswordResetEmail = async (email: string): Promise<void> => {
  try {
    const backendUrl = getBackendUrl();

    const response = await fetch(`${backendUrl}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.message || 'Failed to send password reset email');
    }

    // Success - email was sent (or request was processed for security)
    return Promise.resolve();

  } catch (error) {
    logger.error('Error sending password reset email:', error);
    throw error;
  }
};

export interface SignupPayload {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  state?: string;
  organization_name: string;
  department?: string;
  password: string;
  confirm_password: string;
  timezone?: string;
  join_existing_org?: boolean;
}

// A non-2xx response isn't guaranteed to be JSON (a load-balancer/CDN error page, an
// empty timeout body, ...), so `.json()` is parsed defensively and `detail` is checked
// for shape — FastAPI's own 422s return `detail` as an array of error objects, which
// would otherwise render as "[object Object]".
const parseJsonResponseOrThrow = async <T>(response: Response, fallbackMessage: string): Promise<T> => {
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(typeof result?.detail === 'string' ? result.detail : fallbackMessage);
  }
  return result;
};

export const sendSignupOtp = async (
  email: string
): Promise<{ sent: boolean; message: string; expires_in_seconds: number }> => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/auth/send-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return parseJsonResponseOrThrow(response, 'Could not send the code. Please try again.');
};

export const checkOrgDomain = async (
  email: string
): Promise<{ matched: boolean; organization_name: string | null }> => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/auth/check-org-domain`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return parseJsonResponseOrThrow(response, 'Could not check the organization for this email.');
};

export const checkOrgName = async (
  email: string,
  organizationName: string
): Promise<{ matched: boolean; organization_name: string | null }> => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/auth/check-org-name`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, organization_name: organizationName }),
  });
  return parseJsonResponseOrThrow(response, 'Could not check this organization name.');
};

export const verifySignupOtp = async (email: string, code: string): Promise<{ verified: boolean; message: string }> => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/auth/verify-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  return parseJsonResponseOrThrow(response, 'Verification failed. Please try again.');
};

export const signup = async (payload: SignupPayload): Promise<void> => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.detail || 'Signup failed. Please try again.');
  }
  if (!result.success) {
    throw new Error(result.message || 'Signup failed. Please try again.');
  }
};

export interface CompleteProfilePayload {
  first_name: string;
  last_name: string;
  phone: string;
  state?: string;
  organization_name: string;
  department?: string;
  password: string;
  confirm_password: string;
  timezone?: string;
}

/**
 * Deletes the Keycloak account for a user who is bailing out of SSO signup before
 * finishing `completeProfile` — otherwise the account Keycloak auto-created on
 * first SSO login (with no organization) is orphaned. Call before logging out.
 */
export const cancelSsoSignup = async (token: string): Promise<void> => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/auth/cancel-sso-signup`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error('Failed to cancel signup.');
  }
};

/**
 * Finish setting up an already-authenticated (SSO-created) account: create/join a
 * trial org and complete the trainee profile. Used when a user first logs in via
 * SSO and Keycloak auto-created their account with no organization assigned.
 */
export const completeProfile = async (payload: CompleteProfilePayload, token: string): Promise<void> => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/auth/complete-profile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.detail || 'Account setup failed. Please try again.');
  }
  if (!result.success) {
    throw new Error(result.message || 'Account setup failed. Please try again.');
  }
};

/**
 * Reset password using a token from the reset email link.
 * Calls backend POST /auth/reset-password with the token and new password.
 */
export const resetPassword = async (token: string, newPassword: string): Promise<void> => {
  const backendUrl = getBackendUrl();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(`${backendUrl}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword }),
      signal: controller.signal,
    });
    if (!response.ok) {
      let message = 'Failed to reset password. Please try again.';
      try {
        const err = await response.json();
        message = err.message || message;
      } catch {}
      throw new Error(message);
    }
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.message || 'Failed to reset password');
    }
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  // First check Keycloak instance authentication
  if (keycloakInstance.authenticated) {
    return true;
  }

  // Also check if we have a valid cached token and restore state if needed
  const now = Date.now();
  const expirationThreshold = now + (TOKEN_EXPIRATION_BUFFER * 1000);

  if (tokenCache.token && tokenCache.expirationTime > expirationThreshold) {
    // Restore the Keycloak instance state from cache
    keycloakInstance.token = tokenCache.token;
    keycloakInstance.refreshToken = tokenCache.refreshToken || undefined;
    keycloakInstance.tokenParsed = parseJwt(tokenCache.token);
    // Restore idToken too so logout() can supply id_token_hint and skip Keycloak's prompt.
    keycloakInstance.idToken = tokenCache.idToken || undefined;
    (keycloakInstance as any).authenticated = true;

    // Restore magic link flag from cache
    if (tokenCache.isMagicLink) {
      isMagicLinkAuth = true;
    }
    
    logger.debug('Restored authentication from cached token (isMagicLink:', tokenCache.isMagicLink, ')');
    return true;
  }

  // For magic link tokens with refresh tokens, consider them authenticated even if expired
  // The refresh will happen automatically on next API call
  if (tokenCache.token && tokenCache.refreshToken && tokenCache.isMagicLink) {
    logger.debug('Magic link token present with refresh token, considering authenticated');
    keycloakInstance.token = tokenCache.token;
    keycloakInstance.refreshToken = tokenCache.refreshToken;
    keycloakInstance.tokenParsed = parseJwt(tokenCache.token);
    // Restore idToken too so logout() can supply id_token_hint and skip Keycloak's prompt.
    keycloakInstance.idToken = tokenCache.idToken || undefined;
    (keycloakInstance as any).authenticated = true;
    isMagicLinkAuth = true;
    isKeycloakNativeToken = tokenCache.isKeycloakNative || false;
    return true;
  }

  // If we have an expired token but a refresh token, try to refresh
  if (tokenCache.token && tokenCache.refreshToken && tokenCache.expirationTime <= now) {
    logger.debug('Found expired token with refresh token, attempting refresh');
    // Note: This is async but we're in a sync function
    // The caller should handle this case by calling getIdToken() which will refresh
    return false; // Let getIdToken handle the refresh
  }

  return false;
};

export default keycloakInstance;
